"use client";

import { ChevronDown, Languages, Search, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/routing";
import {
  authors,
  brandLogos,
  menuColumns,
  type MenuGroup,
} from "@/fixtures/shared/nav/more-menu-fixtures";

type MobileMegaMenuProps = {
  open: boolean;
  onClose: () => void;
};

export function MobileMegaMenu({ open, onClose }: MobileMegaMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <aside
        role="dialog"
        aria-label="選單"
        aria-hidden={!open}
        className="fixed left-0 top-0 z-[70] flex h-full w-screen flex-col bg-white transition-transform duration-200 ease-out lg:hidden"
        style={{
          transform: open ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        <MobileMenuHeader onClose={onClose} />
        <div
          className="flex-1 overflow-y-auto"
          style={{ padding: "8px 0 24px" }}
        >
          {menuColumns.flat().map((group) => (
            <CollapsibleGroup key={group.heading} group={group} />
          ))}
          <BrandLogosSection />
          <AuthorsSection />
        </div>
      </aside>
    </>
  );
}

function MobileMenuHeader({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: "16px 20px",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <button
        aria-label="關閉選單"
        onClick={onClose}
        className="flex items-center justify-center"
      >
        <X
          style={{ width: 24, height: 24, color: "#1d1d1f" }}
          strokeWidth={1.8}
        />
      </button>
      <div className="flex items-center" style={{ gap: 16 }}>
        <button aria-label="搜尋" className="flex items-center justify-center">
          <Search
            style={{ width: 22, height: 22, color: "#1d1d1f" }}
            strokeWidth={1.8}
          />
        </button>
        <button aria-label="語言" className="flex items-center justify-center">
          <Languages
            style={{ width: 22, height: 22, color: "#1d1d1f" }}
            strokeWidth={1.8}
          />
        </button>
        <Link
          href="/subscription"
          className="inline-flex items-center justify-center font-bold"
          style={{
            backgroundColor: "#56917d",
            color: "#ffffff",
            padding: "7px 12px",
            borderRadius: 2,
            fontSize: 13,
            lineHeight: 1,
          }}
        >
          立即訂閱
        </Link>
      </div>
    </div>
  );
}

function CollapsibleGroup({ group }: { group: MenuGroup }) {
  const [open, setOpen] = useState(true);
  return (
    <div
      style={{
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between"
        style={{
          padding: "14px 20px",
          fontSize: 16,
          fontWeight: 700,
          color: "#096344",
          lineHeight: "20px",
        }}
      >
        <span>{group.heading}</span>
        <ChevronDown
          style={{
            width: 20,
            height: 20,
            color: "#096344",
            transform: open ? "rotate(180deg)" : undefined,
            transition: "transform 150ms",
          }}
          strokeWidth={2}
        />
      </button>
      {open && (
        <ul
          className="flex flex-col"
          style={{ padding: "0 20px 16px", gap: 10 }}
        >
          {group.links.map((link) => (
            <li key={link.label}>
              <Link
                href={link.href}
                className="block hover:text-[#108a60]"
                style={{
                  color: "#333",
                  fontSize: 15,
                  lineHeight: "22px",
                  paddingInlineStart: 4,
                }}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BrandLogosSection() {
  return (
    <div style={{ borderBottom: "1px solid #f0f0f0" }}>
      <h3
        style={{
          padding: "14px 20px",
          fontSize: 16,
          fontWeight: 700,
          color: "#096344",
          lineHeight: "20px",
        }}
      >
        信報品牌
      </h3>
      <div
        className="flex flex-wrap items-start"
        style={{ padding: "0 20px 16px", gap: 10 }}
      >
        {brandLogos.map((logo) => (
          <Link
            key={logo.alt}
            href={logo.href}
            className="inline-flex items-center"
            style={{
              border: "1px solid #666",
              borderRadius: 3,
              padding: "6px 12px",
              gap: 6,
            }}
          >
            <span
              className="relative block shrink-0"
              style={{ width: logo.width, height: 18 }}
            >
              <Image
                src={logo.src}
                alt={logo.alt}
                fill
                className="object-contain"
                sizes={`${logo.width}px`}
              />
            </span>
            {logo.withText && (
              <span
                className="whitespace-nowrap"
                style={{
                  color: "#333",
                  fontSize: 13,
                  lineHeight: "normal",
                }}
              >
                {logo.withText}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

function AuthorsSection() {
  return (
    <div style={{ borderBottom: "1px solid #f0f0f0" }}>
      <h3
        style={{
          padding: "14px 20px",
          fontSize: 16,
          fontWeight: 700,
          color: "#096344",
          lineHeight: "20px",
        }}
      >
        專欄作家
      </h3>
      <div
        className="grid grid-cols-2"
        style={{ padding: "0 20px 16px", columnGap: 12, rowGap: 14 }}
      >
        {authors.map((a) => (
          <Link
            key={a.name}
            href={a.href}
            className="flex items-center"
            style={{ gap: 10 }}
          >
            <span
              className="relative block shrink-0 overflow-hidden rounded-full"
              style={{
                width: 40,
                height: 40,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <Image
                src={a.avatar}
                alt={a.name}
                fill
                className="object-cover"
                sizes="40px"
              />
            </span>
            <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
              <span
                className="truncate text-black"
                style={{ fontSize: 14, lineHeight: "normal" }}
              >
                {a.name}
              </span>
              {a.column && (
                <span
                  className="truncate"
                  style={{
                    color: "#6f6f6f",
                    fontSize: 12,
                    lineHeight: "normal",
                  }}
                >
                  {a.column}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
